import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { useLanguage } from '../../shell/language-context';
import { MarqueeControls, centreOffset } from './marquee-controls';
import type { HomeFormat } from './use-home-format';

type BusinessSummary = components['schemas']['BusinessSummary'];

function TrendingCard({ business, rank, fmt }: { business: BusinessSummary; rank: number; fmt: HomeFormat }) {
  const { t, lang } = useLanguage();
  const cover = business.cover_image?.['800'] ?? business.cover_image?.['400'];
  const name = lang === 'bn' && business.name_bn ? business.name_bn : business.name;
  const price =
    business.price_from && business.price_from > 0
      ? fmt.money(business.price_from) + fmt.unit(business.price_unit)
      : t('dc50.card.free_reservation');
  return (
    <Link to={`/b/${business.slug}`} className="hm-ocard hm-ocard--trend">
      {cover ? <img className="hm-ocard__img" src={cover} alt={name} loading="lazy" /> : null}
      <span className="hm-ocard__grad hm-ocard__grad--trend" aria-hidden="true" />
      <span className="hm-ocard__rank">🏆 {t('dc50.trend.rank', { n: fmt.num(rank) })}</span>
      <span className="hm-ocard__chip">{fmt.catLabel(business.category_id)}</span>
      <span className="hm-ocard__body">
        <span className="hm-ocard__area">{business.area_name}</span>
        <span className="hm-ocard__biz hm-ocard__biz--trend">{name}</span>
        <span className="hm-ocard__line">
          ★ {fmt.rating(business.rating)} ({fmt.num(business.review_count)}) · {price}
        </span>
      </span>
    </Link>
  );
}

// "Trending right now." (design DC:644-679, FR-C20): the top ten from the trending
// rollup — the same signal as /trending, so a rank here matches the rank there — as a
// looping marquee with play/pause and ten dots, plus the "all trending" call to action.
// Rankings are organic; no placement enters this rail.
export function TrendingRailSection({ items, fmt }: { items: BusinessSummary[]; fmt: HomeFormat }) {
  const { t } = useLanguage();
  const [dot, setDot] = useState<number | null>(null);
  const [shift, setShift] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);
  if (items.length === 0) return null;
  const top = items.slice(0, 10);
  const loop = [...top, ...top];

  const select = (i: number) => {
    const rail = railRef.current;
    const first = rail?.querySelector<HTMLElement>('.hm-ocard');
    setDot(i);
    setShift(centreOffset(i, rail?.clientWidth ?? window.innerWidth, first?.offsetWidth ?? 380));
  };

  return (
    <section className="hm-sec hm-sec--trend" data-reveal="out">
      <div className="hm-head hm-head--mb">
        <div>
          <h2 className="hm-h2 hm-h2--sm">{t('dc50.trend.title')}</h2>
          <div className="hm-sub">{t('dc50.trend.sub', { n: fmt.num(top.length) })}</div>
        </div>
        <Link className="hm-darkbtn" to="/trending">
          {t('dc50.trend.cta')}
        </Link>
      </div>
      <div className="hm-rail hm-rail--flush" ref={railRef}>
        <div
          className="hm-rail__track hm-rail__track--trend"
          data-motion="true"
          data-marquee="true"
          style={dot !== null ? { animation: 'none', transform: `translateX(${shift}px)` } : undefined}
        >
          {loop.map((b, i) => (
            <TrendingCard key={`${b.id}-${i}`} business={b} rank={(i % top.length) + 1} fmt={fmt} />
          ))}
        </div>
      </div>
      <MarqueeControls
        className="hm-mq--trend"
        count={top.length}
        selected={dot}
        onSelect={select}
        onToggle={() => (dot !== null ? setDot(null) : select(0))}
        playLabel={t('dc50.trend.play_pause')}
        dotLabel={(i) => t('dc50.trend.go_to', { n: fmt.num(i + 1) })}
      />
    </section>
  );
}
